// src/lib/resources/glossary/search.ts
//
// Single source of truth for the glossary term ranker.
//
// The ~130-line 14-tier scorer below was copy-pasted BYTE-FOR-BYTE as
// `performAdvancedSearch` inside both `GlossaryHero` and `GlossaryGrid` — the
// exact duplication-by-copy class Phase 1 exists to kill (a scoring tweak had to
// be made in two places or silently drift). It is a pure function with no React
// or DOM dependencies, so it is safe in any client bundle.
//
// The two call sites differed only in their *wrapper* policy, which stays at the
// call site: the hero shows nothing for an empty query and caps results at 8;
// the grid shows every category term for an empty query and does not cap. This
// function owns only the ranking of a NON-EMPTY query and returns every matched
// term, best-first, without the transient score/matchType metadata.

/**
 * Minimal term shape the ranker reads. Both `GlossaryHero`'s `TermCardShape` and
 * `GlossaryGrid`'s `TermCardLike` (each a `Pick<GlossaryTerm, …>`) satisfy it, so
 * the ranker stays generic over the caller's own term type and returns it
 * unchanged.
 */
export interface GlossarySearchableTerm {
  slug: string
  title: string
  shortDescription: string
  category: string
  definition?: string
  acronym?: string
}

/** Which field produced a match (kept for parity; used only for internal ranking). */
export type GlossaryMatchType =
  | 'exact'
  | 'acronym'
  | 'title'
  | 'description'
  | 'definition'
  | 'category'
  | 'slug'

/**
 * Escapes RegExp metacharacters so a raw user query can be safely interpolated
 * into a `new RegExp(...)`. Without this, a query like `c++` or `(test` throws
 * and breaks search (the grid's scoring memo has no try/catch).
 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

interface Scored<T> {
  term: T
  score: number
  matchType: GlossaryMatchType
}

/**
 * Ranks glossary terms against a query using the 14-tier scorer.
 *
 * Scoring priority (highest → lowest): exact title (1000) · exact acronym (900) ·
 * acronym starts-with (850) · acronym contains (800) · title starts-with (600) ·
 * title contains-all-words (500 +100 word-boundary) · title contains (400) ·
 * description starts-with (300) · description contains-all-words (250 +50) ·
 * description contains (200) · definition contains-all-words (150 +25) ·
 * definition contains (100) · category contains (50) · slug contains (25). A
 * length bonus (`max(0, 50 - title.length)`) and a multi-word-in-title bonus are
 * then added. Ties break on shorter title first.
 *
 * Empty/whitespace queries return `[]` — callers decide what an empty query
 * should show (the hero shows nothing, the grid shows all terms). Only matched
 * terms (score > 0) are returned; the score/matchType metadata is stripped.
 *
 * @param terms - Candidate terms (generic over any {@link GlossarySearchableTerm}).
 * @param query - The raw search query.
 * @returns Matched terms, best-first, as the caller's own term type.
 */
export function rankGlossaryTerms<T extends GlossarySearchableTerm>(terms: T[], query: string): T[] {
  const searchLower = query.toLowerCase().trim()
  if (!searchLower) {
    return []
  }

  const searchWords = searchLower.split(/\s+/)
  // Escaped once for the word-boundary regex checks below (query may contain
  // RegExp metacharacters).
  const escapedQuery = escapeRegExp(searchLower)

  // Use a Map to ensure unique results by slug
  const resultsMap = new Map<string, Scored<T>>()

  terms.forEach((term) => {
    let score = 0
    let matchType: GlossaryMatchType = 'definition'

    // Captured as a `const` so its `truthy` narrowing survives into the
    // `.every()` closures below without a non-null assertion.
    const definition = term.definition

    // 1. Exact title match (highest priority)
    if (term.title.toLowerCase() === searchLower) {
      score = 1000
      matchType = 'exact'
    }
    // 2. Exact acronym match (very high priority)
    else if (term.acronym?.toLowerCase() === searchLower) {
      score = 900
      matchType = 'acronym'
    }
    // 3. Acronym starts with search (high priority for partial acronym matches)
    else if (term.acronym?.toLowerCase().startsWith(searchLower)) {
      score = 850
      matchType = 'acronym'
    }
    // 4. Acronym contains search (high priority for partial acronym matches)
    else if (term.acronym?.toLowerCase().includes(searchLower)) {
      score = 800
      matchType = 'acronym'
    }
    // 5. Title starts with search query
    else if (term.title.toLowerCase().startsWith(searchLower)) {
      score = 600
      matchType = 'title'
    }
    // 6. Title contains all search words
    else if (searchWords.every((word) => term.title.toLowerCase().includes(word))) {
      score = 500
      matchType = 'title'
      // Bonus for word boundaries
      if (new RegExp(`\\b${escapedQuery}\\b`, 'i').test(term.title)) {
        score += 100
      }
    }
    // 7. Title contains search query
    else if (term.title.toLowerCase().includes(searchLower)) {
      score = 400
      matchType = 'title'
    }
    // 8. Short description starts with search
    else if (term.shortDescription.toLowerCase().startsWith(searchLower)) {
      score = 300
      matchType = 'description'
    }
    // 9. Short description contains all search words
    else if (searchWords.every((word) => term.shortDescription.toLowerCase().includes(word))) {
      score = 250
      matchType = 'description'
      // Bonus for word boundaries
      if (new RegExp(`\\b${escapedQuery}\\b`, 'i').test(term.shortDescription)) {
        score += 50
      }
    }
    // 10. Short description contains search
    else if (term.shortDescription.toLowerCase().includes(searchLower)) {
      score = 200
      matchType = 'description'
    }
    // 11. Definition contains all search words
    else if (definition && searchWords.every((word) => definition.toLowerCase().includes(word))) {
      score = 150
      matchType = 'definition'
      // Bonus for word boundaries
      if (new RegExp(`\\b${escapedQuery}\\b`, 'i').test(definition)) {
        score += 25
      }
    }
    // 12. Definition contains search
    else if (definition?.toLowerCase().includes(searchLower)) {
      score = 100
      matchType = 'definition'
    }
    // 13. Category matches
    else if (term.category.toLowerCase().includes(searchLower)) {
      score = 50
      matchType = 'category'
    }
    // 14. Slug matches (fallback)
    else if (term.slug.toLowerCase().includes(searchLower)) {
      score = 25
      matchType = 'slug'
    }

    // Apply additional scoring factors for better relevance
    if (score > 0) {
      // Slight preference for shorter terms (more specific)
      const lengthBonus = Math.max(0, 50 - term.title.length)
      score += lengthBonus

      // Bonus for multiple word matches in title
      const titleWords = term.title.toLowerCase().split(/\s+/)
      const matchingWords = searchWords.filter((word) =>
        titleWords.some((titleWord) => titleWord.includes(word)),
      )
      if (matchingWords.length > 1) {
        score += matchingWords.length * 10
      }

      // Only keep the highest scoring result for each slug
      const existing = resultsMap.get(term.slug)
      if (!existing || score > existing.score) {
        resultsMap.set(term.slug, { term, score, matchType })
      }
    }
  })

  // Sort by score (descending), then by title length (ascending) for tie-breaking
  return Array.from(resultsMap.values())
    .sort((a, b) => (b.score !== a.score ? b.score - a.score : a.term.title.length - b.term.title.length))
    .map((result) => result.term)
}
